import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Charge } from "../../models/Charge";
import { ChargeLayer } from "../../models/ChargeLayer";
import {
  upsertCharge,
  openCharge,
  closeCharge,
  deleteCharge,
  upsertChargeLayer,
  deleteChargeLayer,
  reorderChargeLayer,
  setCurrentChargeLayer,
  unsetCurrentChargeLayer,
  createGroupNumber,
  upsertManualTargetWeight,
  upsertHeatWeightRequest,
} from "../../repositories/ChargeRepository";

// =============================================================================
// Charge Mutations
// =============================================================================
// NOTE: Charge mutations do NOT invalidate queries — charges use polling.
// Charge Layer mutations DO invalidate ["charge-layers"] on success
// for immediate UI feedback (polling alone introduces visible lag).

export const useUpsertChargeMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (charge: Charge) => {
      const resp = await upsertCharge(charge);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["charges"] });
    },
  });
};

export const useOpenChargeMutation = () => {
  return useMutation({
    mutationFn: async (chargeId: number) => {
      const resp = await openCharge(chargeId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useCloseChargeMutation = () => {
  return useMutation({
    mutationFn: async (chargeId: number) => {
      const resp = await closeCharge(chargeId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useDeleteChargeMutation = () => {
  return useMutation({
    mutationFn: async (chargeId: number) => {
      const resp = await deleteCharge(chargeId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

// =============================================================================
// Charge Layer Mutations
// =============================================================================

type UpsertChargeLayerPayload = {
  ChargeLayerId?: number | null;
  ChargeId?: number | null;
  CommodityId?: number | null;
  Weight?: number | null;
  IsWeightManual?: boolean;
  LayerNumber?: number | null;
  IsCurrent?: boolean;
};

export const useUpsertChargeLayerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chargeLayer: UpsertChargeLayerPayload) => {
      const resp = await upsertChargeLayer(chargeLayer);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["charge-layers"] });
    },
  });
};

export const useDeleteChargeLayerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chargeLayerId: number) => {
      const resp = await deleteChargeLayer(chargeLayerId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["charge-layers"] });
    },
  });
};

export const useReorderChargeLayerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      chargeLayerId,
      newLayerNumber,
    }: {
      chargeLayerId: number;
      newLayerNumber: number;
    }) => {
      const resp = await reorderChargeLayer(chargeLayerId, newLayerNumber);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["charge-layers"] });
    },
  });
};

export const useSetCurrentChargeLayerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chargeLayerId: number) => {
      const resp = await setCurrentChargeLayer(chargeLayerId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
    onMutate: async (chargeLayerId) => {
      await queryClient.cancelQueries({ queryKey: ["charge-layers"] });
      const snapshot = queryClient.getQueriesData<ChargeLayer[]>({
        queryKey: ["charge-layers"],
      });
      queryClient.setQueriesData<ChargeLayer[]>(
        { queryKey: ["charge-layers"] },
        (old) =>
          old?.map((layer) => ({
            ...layer,
            IsCurrent: layer.ChargeLayerId === chargeLayerId,
          })),
      );
      return { snapshot };
    },
    onError: (_error, _chargeLayerId, context) => {
      context?.snapshot.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
};

export const useUnsetCurrentChargeLayerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (chargeLayerId: number) => {
      const resp = await unsetCurrentChargeLayer(chargeLayerId);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
    onMutate: async (chargeLayerId) => {
      await queryClient.cancelQueries({ queryKey: ["charge-layers"] });
      const snapshot = queryClient.getQueriesData<ChargeLayer[]>({
        queryKey: ["charge-layers"],
      });
      queryClient.setQueriesData<ChargeLayer[]>(
        { queryKey: ["charge-layers"] },
        (old) =>
          old?.map((layer) => ({
            ...layer,
            IsCurrent:
              layer.ChargeLayerId === chargeLayerId ? false : layer.IsCurrent,
          })),
      );
      return { snapshot };
    },
    onError: (_error, _chargeLayerId, context) => {
      context?.snapshot.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
    },
  });
};

// =============================================================================
// Special Mutations
// =============================================================================

type CreateGroupNumberParams = {
  recipeId: number;
  charge1: boolean;
  charge2: boolean;
  charge3: boolean;
  bucketId1: number;
  bucketId2: number;
  bucketId3: number;
  scheduledHeat: string | null;
};

export const useCreateGroupNumberMutation = () => {
  return useMutation({
    mutationFn: async (params: CreateGroupNumberParams) => {
      const resp = await createGroupNumber(
        params.recipeId,
        params.charge1,
        params.charge2,
        params.charge3,
        params.bucketId1,
        params.bucketId2,
        params.bucketId3,
        params.scheduledHeat,
      );
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useUpsertManualTargetWeightMutation = () => {
  return useMutation({
    mutationFn: async ({
      chargeId,
      manualTargetWeight,
    }: {
      chargeId: number;
      manualTargetWeight: number;
    }) => {
      const resp = await upsertManualTargetWeight(chargeId, manualTargetWeight);
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
  });
};

export const useUpsertHeatWeightRequestMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      scheduledHeatName,
      requestedHeatWeight,
    }: {
      scheduledHeatName: string;
      requestedHeatWeight: number | null;
    }) => {
      const resp = await upsertHeatWeightRequest(
        scheduledHeatName,
        requestedHeatWeight,
      );
      if (!resp.ok) throw new Error(resp.message);
      return resp;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["charges"] });
    },
  });
};
